import * as THREE from 'three';
import { scene, camera } from './scene.js';
import { particles, decals, tracers, splats } from './state.js';

// ============================================================
//  Эффекты: искры, дырки от пуль, трассеры, числа урона, kill-feed.
// ============================================================

export function spawnSparks(pos, normal, color, n = 8) {
  for (let i = 0; i < n; i++) {
    const m = new THREE.Mesh(new THREE.SphereGeometry(0.03, 4, 4),
      new THREE.MeshBasicMaterial({ color, blending: THREE.AdditiveBlending, transparent: true }));
    m.position.copy(pos);
    const v = normal.clone().multiplyScalar(2 + Math.random() * 2)
      .add(new THREE.Vector3(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5).multiplyScalar(3));
    m.userData = { vel: v, life: 0.4 };
    scene.add(m); particles.push(m);
  }
}

/**
 * Лёгкий дымок из ствола после выстрела — пара серых клубов, которые всплывают
 * и растворяются за ~0.4 с. НЕ аддитивный (аддитивный серый бы светился как
 * пламя): обычная полупрозрачность с низким потолком `op`, чтобы в упор из-за
 * камеры дым не превращался в мутный ком, особенно при автоочереди винтовки.
 * @param {THREE.Vector3} pos мировая точка дула
 * @param {THREE.Vector3} dir направление выстрела (клубы сносит вперёд+вверх)
 */
export function spawnMuzzleSmoke(pos, dir) {
  for (let i = 0; i < 2; i++) {
    const m = new THREE.Mesh(new THREE.SphereGeometry(0.05, 6, 6),
      new THREE.MeshBasicMaterial({ color: 0x8f8f8f, transparent: true, opacity: 0, depthWrite: false }));
    m.position.copy(pos).addScaledVector(dir, 0.12 + i * 0.05)
      .add(new THREE.Vector3((Math.random() - 0.5) * 0.05, (Math.random() - 0.5) * 0.05, (Math.random() - 0.5) * 0.05));
    const life = 0.32 + Math.random() * 0.2;
    m.userData = {
      vel: dir.clone().multiplyScalar(1.0).add(new THREE.Vector3(0, 0.6, 0)),
      life, life0: life, gravity: -1.4, drag: 2.6, grow: 3.2, op: 0.3,
    };
    scene.add(m); particles.push(m);
  }
}

/**
 * Богатый взрыв осколочной гранаты — чисто клиентская косметика (на урон,
 * который считает сервер, не влияет). В отличие от spawnSparks здесь искры
 * реально разлетаются по всей зоне поражения: высокая начальная скорость
 * (до ~40 м/с) с воздушным сопротивлением уводит их на 10–15 м за полсекунды.
 *
 * Состоит из четырёх слоёв, каждый — обычная частица из общего движка
 * updateFx (см. поля userData ниже):
 *   • ядро вспышки — яркая сфера, раздувается и гаснет за ~0.12 с;
 *   • огненные клубы — оранжевые сферы вразнобой, живут дольше ядра;
 *   • осколки — быстрые искры с гравитацией и сопротивлением, сжимаются к концу;
 *   • ударная волна — плоское кольцо по земле, разбегается до радиуса зоны.
 *
 * Все слои — аддитивные и яркие, поэтому bloom (порог 1.0) их подсвечивает.
 * @param {THREE.Vector3} pos центр взрыва
 * @param {number} radius радиус зоны поражения (докуда добегает ударная волна)
 */
export function spawnBoom(pos, radius = 16) {
  const up = new THREE.Vector3(0, 1, 0);
  const core = pos.clone().addScaledVector(up, 0.3);

  // ---- ядро вспышки: два раздувающихся аддитивных шара ----
  addGlow(core, 0.9, 0xffe6a0, { grow: 22, life: 0.13 });
  addGlow(core, 0.5, 0xffffff, { grow: 26, life: 0.09 });

  // ---- огненные клубы вокруг ядра ----
  for (let i = 0; i < 6; i++) {
    const off = new THREE.Vector3(Math.random() - 0.5, Math.random() * 0.7, Math.random() - 0.5)
      .multiplyScalar(1.3);
    addGlow(core.clone().add(off), 0.5 + Math.random() * 0.5, 0xff7a1e,
      { grow: 5 + Math.random() * 4, life: 0.28 + Math.random() * 0.22, gravity: -1.5 });
  }

  // ---- осколки: летят далеко, покрывая зону поражения ----
  const N = 72;
  for (let i = 0; i < N; i++) {
    const dir = new THREE.Vector3(Math.random() - 0.5, Math.random() * 0.9 - 0.12, Math.random() - 0.5)
      .normalize();
    const speed = 14 + Math.random() * 26;             // до ~40 м/с
    // цвет от белого/жёлтого у быстрых к оранжево-красному у медленных
    const hot = speed > 30;
    const m = new THREE.Mesh(new THREE.SphereGeometry(0.035, 4, 4),
      new THREE.MeshBasicMaterial({
        color: hot ? 0xfff0b0 : 0xff6a1a,
        blending: THREE.AdditiveBlending, transparent: true,
      }));
    m.position.copy(core);
    const life = 0.5 + Math.random() * 0.7;
    m.userData = { vel: dir.multiplyScalar(speed), life, life0: life, gravity: 16, drag: 2.2, shrink: true };
    scene.add(m); particles.push(m);
  }

  // ---- ударная волна: плоское кольцо по земле, разбегается до радиуса ----
  const ring = new THREE.Mesh(
    new THREE.RingGeometry(0.6, 1.0, 48),
    new THREE.MeshBasicMaterial({
      color: 0xffb15a, side: THREE.DoubleSide, blending: THREE.AdditiveBlending,
      transparent: true, depthWrite: false,
    }));
  // ⚠️ Высота — от точки взрыва, а не от нуля. Раньше стояло `setY(0.14)`
  // («чуть выше пола арены»), и на карте с перепадом 7.6 м волна от взрыва
  // на террасе расходилась кольцом во дворе под ней — см. баг-урок
  // «гранаты проваливаются под землю» в md/netcode.md.
  ring.position.copy(pos).setY(pos.y + 0.04);
  ring.rotation.x = -Math.PI / 2;
  const rlife = 0.55;
  // кольцо стартует радиусом ~1 м; grow подобран так, чтобы к концу жизни оно
  // раздулось примерно до radius (scale ≈ radius)
  ring.userData = { vel: new THREE.Vector3(), life: rlife, life0: rlife, gravity: 0, grow: radius / rlife };
  scene.add(ring); particles.push(ring);
}

/**
 * Пламя на ГОРЯЩЕМ ЧЕЛОВЕКЕ — по биту `F_BURNING` в снапшоте (протокол 18).
 *
 * Живёт здесь, а не в `grenades.js`, где вся остальная работа с огнём: рисует
 * его `remote.js`, и импорт из `grenades.js` замкнул бы цикл модулей
 * (grenades → player → … → remote → grenades) — тот самый класс TDZ-ошибок,
 * который не ловят ни линт, ни сборка, ни тесты.
 *
 * @param {number} x,y,z точка НОГ горящего
 * @param {number} h рост (высота глаз): языки идут по всему телу, а не у пят
 * @param {number} dt кадровое время — плотность не должна зависеть от FPS
 */
export function spawnBodyFire(x, y, z, h, dt) {
  // ~14 язычков в секунду: заметно издалека, но не превращает человека
  // в костёр, за которым не видно, куда он смотрит.
  let n = 14 * dt;
  n = Math.floor(n) + (Math.random() < n % 1 ? 1 : 0);
  for (let i = 0; i < n; i++) {
    const size = 0.06 + Math.random() * 0.06;
    const m = new THREE.Mesh(new THREE.SphereGeometry(size, 5, 4),
      new THREE.MeshBasicMaterial({
        color: Math.random() < 0.5 ? 0xffd166 : 0xff8c1a,
        blending: THREE.AdditiveBlending, transparent: true, depthWrite: false,
      }));
    // по телу: чуть шире у ног, уже у головы
    const up = Math.random() * h;
    const r = 0.28 * (1 - 0.4 * (up / h));
    const a = Math.random() * 6.283;
    m.position.set(x + Math.cos(a) * r, y + up, z + Math.sin(a) * r);
    const life = 0.25 + Math.random() * 0.2;
    m.userData = {
      vel: new THREE.Vector3((Math.random() - 0.5) * 0.4, 1.1 + Math.random() * 0.7, (Math.random() - 0.5) * 0.4),
      life, life0: life, gravity: -1.0, drag: 1.4, shrink: true,
    };
    scene.add(m); particles.push(m);
  }
}

/** Вспомогательное: аддитивная сфера-свечение для ядра взрыва и клубов огня. */
function addGlow(pos, size, color, opts) {
  const m = new THREE.Mesh(new THREE.SphereGeometry(size, 10, 8),
    new THREE.MeshBasicMaterial({ color, blending: THREE.AdditiveBlending, transparent: true, depthWrite: false }));
  m.position.copy(pos);
  m.userData = {
    vel: new THREE.Vector3(), life: opts.life, life0: opts.life,
    gravity: opts.gravity ?? 0, grow: opts.grow ?? 0,
  };
  scene.add(m); particles.push(m);
}

export function addDecal(pos, normal) {
  const m = new THREE.Mesh(new THREE.CircleGeometry(0.06, 10),
    new THREE.MeshBasicMaterial({
      color: 0x0a0a0a, transparent: true, opacity: 0.9, depthWrite: false,
      polygonOffset: true, polygonOffsetFactor: -4,
    }));
  m.position.copy(pos).addScaledVector(normal, 0.01);
  m.lookAt(pos.clone().add(normal));
  scene.add(m); decals.push(m);
  // ⚠️ Потолок поднят с 60: стрелять теперь есть кому кроме игрока, и при
  // шести ботах старый лимит вычищал дырки быстрее, чем игрок успевал
  // повернуть голову, — то есть ровно ту информацию, ради которой чужие
  // дырки и появились («сюда только что стреляли»).
  if (decals.length > 140) {
    const old = decals.shift(); scene.remove(old); old.geometry.dispose(); old.material.dispose();
  }
}

// ============================================================
//  Кровь на стене за пробитым телом
//
//  Пуля прошивает человека (протокол 19) и кончается в стене. Если стена
//  оказалась сразу за ним — значит, стреляли в прижавшегося к ней, и брызги
//  до этой стены долетают: на ней остаётся пятно.
//
//  Всё — чистая клиентская косметика, как искры и дырки: урон и попадание
//  посчитал сервер ещё до того, как кадр ушёл в сеть, и пятно на него не
//  влияет ничем. Поэтому и решение «ставить или нет» принимается здесь, в
//  одной функции: её зовут ОБА места, где рисуется выстрел, — свой
//  (`weapons.js::tryShoot`) и чужой (`combat.js`, кадр `OP_SHOT`). Разложить
//  ту же проверку по двум местам — верный способ получить пятна только от
//  своих выстрелов и потом искать, почему.
// ============================================================

/** Докуда за телом ставим пятно, метры ПО ЛУЧУ.
 *
 *  ⚠️ Мерится от точки ВХОДА пули в тело (её и даёт `bodiesAlong`), а не от
 *  спины: сам человек по лучу занимает 0.3…0.6 м, поэтому 1.6 здесь — это
 *  примерно «метр между спиной и стеной», как и просил владелец. Крутить,
 *  глядя на экран, а не на рулетку. */
const SPLAT_MAX = 1.6;

/** Сколько живёт пятно и за сколько гаснет в конце. Дольше трупа (10 с) и
 *  примерно как лужа крови (15 с): пятно на стене — след боя, по которому
 *  прибежавший на звук понимает, что здесь только что кого-то положили. */
const SPLAT_LIFE = 18.0, SPLAT_FADE = 3.0;

/** Предел одновременных пятен. Мало намеренно: пятно вчетверо больше дырки
 *  от пули и полупрозрачное — сотня таких на экране это уже не «следы боя»,
 *  а красный туман поверх стены.
 *
 *  ⚠️ Считается В ПЯТНАХ, а одно попадание теперь ставит их до `SPLAT_NEAR_GUSH`
 *  (см. ниже). Число поднято ровно во столько же раз, чтобы предел остался
 *  прежним по СОБЫТИЯМ: 6 попаданий в упор, как и было. */
const SPLAT_MAX_N = 54;

const SPLAT_ALPHA = 0.9;

/** Сколько разных клякс держим в кэше — как у лужи крови (corpse.js). */
const SPLAT_VARIANTS = 5;

const splatTex = [];

/**
 * Случайная клякса-брызги на холсте. От лужи под трупом отличается тем, что
 * это ВЕРТИКАЛЬНАЯ поверхность: ядро мельче, вокруг разлёт капель (пуля
 * ударила с силой, а не натекло), а снизу — пара потёков. Ради потёков
 * пятно и разворачивается по мировому «вверх» (см. `spawnBloodSplat`).
 */
function makeSplatTexture() {
  // ⚠️ Холст крупнее лужи (там 128): лужу видно сверху и издалека, а пятно на
  // стене разглядывают в упор — на 128 капли расплываются в мыло. Все размеры
  // ниже выражены через N и base, поэтому число можно крутить, кроме `blur`:
  // он в пикселях холста и при росте N становится относительно мягче.
  const N = 192, cv = document.createElement('canvas');
  cv.width = cv.height = N;
  const g = cv.getContext('2d');
  // Центр выше середины холста: снизу нужно место, куда стекают потёки.
  const cx = N / 2, cy = N * 0.40, base = N * 0.115;

  // Мягкий край: без размытия пятно выглядит вырезанным ножницами.
  g.filter = 'blur(2px)';

  // ---- ядро: клякса с гуляющим по углу радиусом (как у лужи) ----
  const a1 = Math.random() * 6.283, a2 = Math.random() * 6.283;
  const k1 = 2 + Math.floor(Math.random() * 2);
  const k2 = 4 + Math.floor(Math.random() * 3);
  g.beginPath();
  for (let i = 0; i <= 48; i++) {
    const t = (i / 48) * 6.283;
    const r = base * (1 + 0.3 * Math.sin(k1 * t + a1) + 0.15 * Math.sin(k2 * t + a2));
    const x = cx + Math.cos(t) * r, y = cy + Math.sin(t) * r;
    if (i === 0) g.moveTo(x, y); else g.lineTo(x, y);
  }
  g.closePath();
  const grad = g.createRadialGradient(cx, cy, base * 0.15, cx, cy, base * 1.3);
  grad.addColorStop(0, '#7a1010');       // серёдка гуще
  grad.addColorStop(1, '#380707');
  g.fillStyle = grad;
  g.fill();

  // ---- разлёт капель: чем дальше от ядра, тем мельче ----
  const drops = 26 + Math.floor(Math.random() * 16);
  for (let i = 0; i < drops; i++) {
    const t = Math.random() * 6.283;
    const far = Math.random();
    const d = base * (1.2 + far * 2.6);
    const rr = base * (0.30 - 0.21 * far) * (0.6 + Math.random() * 0.8);
    if (rr < 0.5) continue;
    g.fillStyle = (i % 3) ? '#5c0a0a' : '#3d0606';
    g.beginPath();
    // капля вытянута ВДОЛЬ разлёта — так читается «брызнуло», а не «накапало»
    g.ellipse(cx + Math.cos(t) * d, cy + Math.sin(t) * d, rr, rr * 0.55, t, 0, 6.283);
    g.fill();
  }

  // ---- потёки вниз: главное отличие стены от пола ----
  g.fillStyle = '#4a0808';
  const runs = 2 + Math.floor(Math.random() * 3);
  for (let i = 0; i < runs; i++) {
    const x = cx + (Math.random() - 0.5) * base * 1.8;
    const y0 = cy + base * 0.5;
    const len = base * (1.0 + Math.random() * 2.6);
    const w = base * (0.09 + Math.random() * 0.09);
    g.beginPath();
    g.moveTo(x - w, y0);
    g.lineTo(x + w, y0);
    g.lineTo(x + w * 0.3, y0 + len);     // струйка сужается книзу
    g.lineTo(x - w * 0.3, y0 + len);
    g.closePath();
    g.fill();
    g.beginPath();
    g.arc(x, y0 + len, w * 0.5, 0, 6.283);   // капля на конце потёка
    g.fill();
  }

  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;   // холст нарисован в sRGB
  return tex;
}

function pickSplatTex() {
  if (splatTex.length < SPLAT_VARIANTS) splatTex.push(makeSplatTexture());
  return splatTex[Math.floor(Math.random() * splatTex.length)];
}

/** Плоскость пятна — одна геометрия на все (размер задаётся масштабом). */
const SPLAT_GEO = new THREE.PlaneGeometry(1, 1);

const _look = new THREE.Vector3();
// Рабочие векторы для осей стены (см. `spawnBloodSplat`) — общие и
// переиспользуемые: пятна ставятся очередями по три, новые Vector3 на каждое
// кормили бы GC ровно в момент перестрелки.
const _tmpA = new THREE.Vector3(), _tmpB = new THREE.Vector3(), _tmpC = new THREE.Vector3();

/** Во сколько раз больше крови остаётся на стене, если человек стоял к ней
 *  ВПЛОТНУЮ (требование владельца 2026-07-29: «крови должно быть в 3 раза
 *  больше, когда человек рядом со стеной»).
 *
 *  ⚠️ Множитель именно на КОЛИЧЕСТВО крови, то есть на число пятен, а не
 *  на размер одного: втрое больший спрайт — это одна огромная клякса
 *  с растянутой в мыло текстурой, а три пятна рядом читаются как «залило».
 *  На `SPLAT_MAX` множитель сходится к 1 — там человек стоял на открытом
 *  месте, и брызгам до стены далеко. */
const SPLAT_NEAR_GUSH = 3;

/**
 * Пятно крови на стене за пробитым телом.
 *
 * Ничего не делает, если стены нет или она дальше `SPLAT_MAX` — то есть
 * человек стоял на открытом месте.
 *
 * @param {THREE.Vector3} body точка входа пули в тело (`remote.js::bodiesAlong`)
 * @param {?THREE.Vector3} wall точка, где пуля кончилась; null — стены не было
 * @param {THREE.Vector3} normal нормаль стены в этой точке
 */
export function spawnBloodSplat(body, wall, normal) {
  if (!wall) return;
  const d = body.distanceTo(wall);
  if (d > SPLAT_MAX) return;
  const t = d / SPLAT_MAX;

  // Сколько пятен ставим: вплотную к стене — `SPLAT_NEAR_GUSH`, на пределе
  // дальности — одно, как было раньше.
  const n = Math.max(1, Math.round(1 + (SPLAT_NEAR_GUSH - 1) * (1 - t)));

  // Оси стены, вдоль которых расходятся лишние пятна. Берём любые две,
  // перпендикулярные нормали: важно лишь, чтобы разлёт шёл ПО стене, а не
  // сквозь неё (иначе пятно повиснет в воздухе или утонет в геометрии).
  const up = Math.abs(normal.y) > 0.9 ? _tmpA.set(0, 0, 1) : _tmpA.set(0, 1, 0);
  const ax = _tmpB.copy(up).cross(normal).normalize();
  const az = _tmpC.copy(normal).cross(ax).normalize();

  for (let i = 0; i < n; i++) {
    const m = new THREE.Mesh(SPLAT_GEO, new THREE.MeshBasicMaterial({
      map: pickSplatTex(), transparent: true, opacity: 0, depthWrite: false,
      // ⚠️ Смещение МЕНЬШЕ, чем у дырки от пули (-4 и 0.01 в addDecal): пятно
      // должно лежать ПОД дыркой, а не закрывать её — они всегда в одной точке.
      polygonOffset: true, polygonOffsetFactor: -3,
    }));
    // Ближе к стене — пятно плотнее и компактнее, дальше — шире и бледнее:
    // брызги успевают разлететься конусом.
    const size = (0.85 + 0.75 * t) * (0.9 + Math.random() * 0.25);

    // ⚠️ Смещение по стене растёт от номера, а не случайно: первое пятно
    // всегда точно в точке попадания (под дыркой от пули), остальные —
    // разлёт вокруг него. Иначе кровь уезжала бы от дырки целиком.
    // Шаг МЕНЬШЕ размера пятна — они обязаны слипаться в одно залитое место,
    // а не стоять тремя отдельными кляксами.
    m.position.copy(wall).addScaledVector(normal, 0.007 + i * 0.002);
    if (i > 0) {
      const a = Math.random() * 6.283, off = size * (0.25 + Math.random() * 0.3);
      m.position.addScaledVector(ax, Math.cos(a) * off).addScaledVector(az, Math.sin(a) * off);
    }

    if (Math.abs(normal.y) > 0.9) {
      // Пол или потолок: «низа» у такой плоскости нет, потёки рисовать некуда —
      // просто крутим наугад. lookAt здесь ещё и вырождается (нормаль совпадает
      // с up), поэтому кладём плоскость руками.
      m.rotation.set(normal.y > 0 ? -Math.PI / 2 : Math.PI / 2, 0, 0);
      m.rotateZ(Math.random() * 6.283);
    } else {
      // Стена: lookAt держит локальный «вверх» по мировому, и потёки текут вниз.
      m.lookAt(_look.copy(m.position).add(normal));
    }

    m.scale.set(size, size, 1);

    scene.add(m);
    splats.push({ o: m, mat: m.material, age: 0, alpha: SPLAT_ALPHA * (1 - 0.3 * t) });
    if (splats.length > SPLAT_MAX_N) releaseSplat(splats.shift());
  }
}

/** ⚠️ Геометрия и текстура ОБЩИЕ (кэш `splatTex`) — освобождаем только
 *  материал, иначе следующее пятно останется без карты. */
function releaseSplat(s) {
  scene.remove(s.o);
  s.mat.dispose();
}

// ---- Трассеры ----
//
// Раньше трассер был одной `THREE.Line` толщиной в пиксель и жил 0.08 с.
// Своего выстрела этого хватало (он и так в центре экрана), а вот чужой
// увидеть было почти невозможно: жалоба владельца «не понимаю, откуда
// стреляют» — про это. Причина не в яркости, а в геометрии: `linewidth`
// у линий в WebGL не работает вообще (спецификация позволяет драйверу
// игнорировать его, и все игнорируют), поэтому «сделать линию толще»
// нельзя в принципе — нужна настоящая геометрия.
//
// Теперь трассер состоит из трёх слоёв:
//   • ЯДРО — та же линия, даёт чёткую центральную нить;
//   • ЛУЧ — тонкий цилиндр вдоль траектории, аддитивный: он и виден
//     боковым зрением, и подсвечивается блумом;
//   • ВСПЫШКА ВЫЛЕТА — шарик в точке выстрела. Именно он отвечает
//     на вопрос «откуда стреляют»: линия показывает направление,
//     а вспышка — конкретную точку.

/** Толщина луча и цвет по оружию (0 пистолет, 1 винтовка, 2 снайперка). */
const TRACER_STYLE = [
  { r: 0.022, color: 0xffe9a0, life: 0.10 },
  { r: 0.028, color: 0xfff2a8, life: 0.12 },
  { r: 0.045, color: 0xbfe4ff, life: 0.22 },   // снайперка: толще, холоднее, дольше
];

const _up = new THREE.Vector3(0, 1, 0);
const _seg = new THREE.Vector3();

/**
 * @param {THREE.Vector3} a откуда вылетела пуля
 * @param {THREE.Vector3} b куда прилетела
 * @param {number} weapon 0..2 — стиль луча
 * @param {boolean} mine свой выстрел рисуем скромнее: он в центре экрана,
 *   и толстый луч перед носом только мешает целиться
 */
export function spawnTracer(a, b, weapon = 1, mine = false) {
  const st = TRACER_STYLE[weapon] || TRACER_STYLE[1];
  const len = _seg.copy(b).sub(a).length();
  if (!(len > 0.05)) return;

  // ---- ядро ----
  const line = new THREE.Line(new THREE.BufferGeometry().setFromPoints([a, b]),
    new THREE.LineBasicMaterial({ color: st.color, transparent: true, opacity: 0.95 }));
  scene.add(line);
  tracers.push({ o: line, life: st.life, life0: st.life, op: 0.95 });

  // ---- луч ----
  // radialSegments = 5: цилиндр в 3 см толщиной, на него смотрят доли
  // секунды — грани не различимы, а треугольников втрое меньше.
  const r = st.r * (mine ? 0.5 : 1);
  const beam = new THREE.Mesh(new THREE.CylinderGeometry(r, r, len, 5, 1, true),
    new THREE.MeshBasicMaterial({
      color: st.color, transparent: true, opacity: mine ? 0.25 : 0.5,
      blending: THREE.AdditiveBlending, depthWrite: false,
    }));
  beam.position.copy(a).addScaledVector(_seg, 0.5);
  beam.quaternion.setFromUnitVectors(_up, _seg.normalize());
  scene.add(beam);
  tracers.push({ o: beam, life: st.life, life0: st.life, op: mine ? 0.25 : 0.5 });

  // ---- вспышка вылета ----
  // Только у ЧУЖИХ: своя вспышка уже нарисована вьюмоделью (`flash` у ствола),
  // а вторая, в мировых координатах, засветила бы прицел.
  if (!mine) {
    addGlow(a, st.r * 6, 0xffd27a, { grow: 3, life: 0.07 });
  }
}

// ---- Проекция 3D→2D для чисел урона ----
const _p = new THREE.Vector3();
function screenPos(world) {
  _p.copy(world).project(camera);
  return { x: (_p.x * 0.5 + 0.5) * innerWidth, y: (-_p.y * 0.5 + 0.5) * innerHeight, vis: _p.z < 1 };
}

export function spawnDamage(world, dmg, head) {
  const s = screenPos(world); if (!s.vis) return;
  const el = document.createElement('div'); el.className = 'dmg' + (head ? ' head' : '');
  el.textContent = (head ? '☠ ' : '') + dmg; el.style.left = s.x + 'px'; el.style.top = s.y + 'px';
  document.getElementById('hud').appendChild(el);
  el.addEventListener('animationend', () => el.remove());
}

export function addKill() {
  const el = document.createElement('div'); el.className = 'k'; el.textContent = '☠ Бот ликвидирован';
  const kf = document.getElementById('killfeed'); kf.prepend(el);
  setTimeout(() => el.remove(), 4000);
}

export function showHitmarker() {
  const ch = document.getElementById('crosshair');
  ch.classList.add('hit'); setTimeout(() => ch.classList.remove('hit'), 90);
}

export function updateFx(dt, guns) {
  // Вспышки дул гаснут. Пламя (карточки+конус) живёт ~0.15 с как раньше; свет
  // гаснет заметно быстрее — это всполох на кадр-два, а не фонарик у дула.
  const kFlame = Math.pow(0.0001, dt);
  const kLight = Math.pow(1e-13, dt);
  for (const key in guns) {
    const u = guns[key].userData;
    for (const p of u.flashParts) p.material.opacity *= kFlame;
    u.flashLight.intensity *= kLight;
  }
  // трассеры (ядро, луч и вспышка живут в списке одинаковыми записями:
  // движку всё равно, линия там или меш — он гасит прозрачность и убирает)
  for (let i = tracers.length - 1; i >= 0; i--) {
    const t = tracers[i]; t.life -= dt;
    t.o.material.opacity = Math.max(0, t.life / t.life0) * t.op;
    if (t.life <= 0) {
      scene.remove(t.o); t.o.geometry.dispose(); t.o.material.dispose(); tracers.splice(i, 1);
    }
  }
  // пятна крови на стенах: проступают за четверть секунды (кровь долетает не
  // мгновенно), потом висят и гаснут в конце жизни
  for (let i = splats.length - 1; i >= 0; i--) {
    const s = splats[i];
    s.age += dt;
    if (s.age >= SPLAT_LIFE) { releaseSplat(s); splats.splice(i, 1); continue; }
    const left = SPLAT_LIFE - s.age;
    s.mat.opacity = s.alpha * Math.min(1, s.age / 0.25)
      * (left < SPLAT_FADE ? left / SPLAT_FADE : 1);
  }
  // частицы (общий движок: искры пуль, слои взрыва, ударная волна).
  // Поля userData необязательны — старые искры без них ведут себя как прежде:
  //   life0  — начальная жизнь для затухания (по умолчанию 0.4, как у пуль)
  //   gravity— м/с², по умолчанию 9 (вниз); у клубов огня может быть <0 (всплывают)
  //   drag   — воздушное сопротивление, 1/с (скорость гаснет со временем)
  //   grow   — прирост масштаба в секунду (раздувание ядра, огня, кольца)
  //   shrink — сжимать частицу пропорционально остатку жизни (осколки)
  for (let i = particles.length - 1; i >= 0; i--) {
    const p = particles[i], u = p.userData;
    u.life -= dt;
    const life0 = u.life0 ?? 0.4;
    u.vel.y -= (u.gravity ?? 9) * dt;
    if (u.drag) u.vel.multiplyScalar(Math.max(0, 1 - u.drag * dt));
    p.position.addScaledVector(u.vel, dt);
    if (u.grow) p.scale.setScalar(p.scale.x + u.grow * dt);
    else if (u.shrink) p.scale.setScalar(Math.max(0.12, u.life / life0));
    //   op — потолок прозрачности (по умолчанию 1). Нужен полупрозрачным
    //   частицам вроде дыма: движок иначе стартует их с opacity=1 (сплошными).
    p.material.opacity = Math.max(0, u.life / life0) * (u.op ?? 1);
    if (u.life <= 0) {
      scene.remove(p); p.geometry.dispose(); p.material.dispose(); particles.splice(i, 1);
    }
  }
}
